import React, { FC, useContext, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
    Box,
    Button,
    Chip,
    IconButton,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import {
    clinicsForOAuthAppsTemplate,
    createOAuthAppTemplate,
    deleteOAuthAppTemplate,
    getOAuthAppsTemplate,
    rotateOAuthAppSecretTemplate,
    setOAuthAppStatusTemplate,
    updateOAuthAppTemplate,
} from 'apollo/templates/oauth-app';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import Loader from 'components/shared/loader/loader.component';
import {
    CreateOAuthAppInput,
    OAuthApp,
    OAuthAppStatus,
    OAuthAppWithSecret,
    UpdateOAuthAppInput,
} from 'models/oauth-app';
import CreateOAuthAppDialog from './create-oauth-app-dialog.component';
import EditOAuthAppDialog from './edit-oauth-app-dialog.component';
import OAuthAppCredentialsDialog from './oauth-app-credentials-dialog.component';
import { ClinicOption } from './types';

type PendingAction = { type: 'rotate'; app: OAuthApp } | { type: 'delete'; app: OAuthApp };

const CLINIC_FETCH_LIMIT = 1000;

const OAuthApps: FC = () => {
    const { t: translate } = useTranslation(['app-settings', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);

    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [editingApp, setEditingApp] = useState<OAuthApp | null>(null);
    const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
    const [revealedCredentials, setRevealedCredentials] = useState<OAuthAppWithSecret | null>(null);

    const {
        data: appsData,
        loading: appsLoading,
        refetch: refetchApps,
    } = useQuery<{ oauthApps: OAuthApp[] }>(getOAuthAppsTemplate, {
        fetchPolicy: 'network-only',
        onError: showBoundary,
    });
    const apps = appsData?.oauthApps ?? [];

    const { data: clinicsData, loading: clinicsLoading } = useQuery<{ parentClinics: ClinicOption[] }>(
        clinicsForOAuthAppsTemplate,
        {
            variables: {
                filters: { freeText: '', active: true },
                limit: CLINIC_FETCH_LIMIT,
                offset: 0,
            },
            onError: showBoundary,
        },
    );
    const clinics = clinicsData?.parentClinics ?? [];
    const clinicNamesById = new Map(clinics.map((clinic) => [clinic._id, clinic.name]));

    const showToast = (message: string) => {
        setAppToasterContent({ isOpen: true, severity: 'success', message });
    };

    const [createOAuthApp] = useMutation<{ createOAuthApp: OAuthAppWithSecret }>(createOAuthAppTemplate, {
        onCompleted: ({ createOAuthApp: result }) => {
            setRevealedCredentials(result);
            refetchApps();
            showToast(translate('app-settings:OAUTH_APP_CREATED'));
        },
        onError: showBoundary,
    });

    const [rotateSecret] = useMutation<{ rotateOAuthAppSecret: OAuthAppWithSecret }>(rotateOAuthAppSecretTemplate, {
        onCompleted: ({ rotateOAuthAppSecret: result }) => {
            setRevealedCredentials(result);
            refetchApps();
            showToast(translate('app-settings:OAUTH_APP_ROTATED'));
        },
        onError: showBoundary,
    });

    const [updateApp] = useMutation(updateOAuthAppTemplate, {
        onCompleted: () => {
            refetchApps();
            showToast(translate('app-settings:OAUTH_APP_UPDATED'));
        },
        onError: showBoundary,
    });

    const [setStatus] = useMutation(setOAuthAppStatusTemplate, {
        onCompleted: () => {
            refetchApps();
            showToast(translate('app-settings:OAUTH_APP_UPDATED'));
        },
        onError: showBoundary,
    });

    const [deleteApp] = useMutation(deleteOAuthAppTemplate, {
        onCompleted: () => {
            refetchApps();
            showToast(translate('app-settings:OAUTH_APP_DELETED'));
        },
        onError: showBoundary,
    });

    const handleCreate = (input: CreateOAuthAppInput) => {
        createOAuthApp({ variables: { input } });
    };

    const handleUpdate = (id: string, input: UpdateOAuthAppInput) => {
        setEditingApp(null);
        updateApp({ variables: { id, input } });
    };

    const handleConfirmPending = () => {
        if (!pendingAction) return;
        if (pendingAction.type === 'rotate') {
            rotateSecret({ variables: { id: pendingAction.app._id } });
        } else if (pendingAction.type === 'delete') {
            deleteApp({ variables: { id: pendingAction.app._id } });
        }
        setPendingAction(null);
    };

    const handleToggleStatus = (app: OAuthApp) => {
        setStatus({
            variables: {
                id: app._id,
                status: app.status === OAuthAppStatus.ACTIVE ? OAuthAppStatus.DISABLED : OAuthAppStatus.ACTIVE,
            },
        });
    };

    return (
        <Box>
            <Typography variant="h2" gutterBottom>
                {translate('app-settings:OAUTH_APPS')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {translate('app-settings:OAUTH_APPS_HELP')}
            </Typography>

            <Box display="flex" gap={2} alignItems="center" mb={3}>
                <Button variant="contained" onClick={() => setIsCreateOpen(true)}>
                    {translate('app-settings:CREATE_OAUTH_APP')}
                </Button>
            </Box>

            {appsLoading && <Loader />}

            {!appsLoading && (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('app-settings:OAUTH_APP_NAME')}</TableCell>
                                <TableCell>{translate('app-settings:OAUTH_APP_VENDOR')}</TableCell>
                                <TableCell>{translate('app-settings:OAUTH_APP_CLIENT_ID')}</TableCell>
                                <TableCell>{translate('app-settings:OAUTH_APP_TYPE')}</TableCell>
                                <TableCell>{translate('app-settings:OAUTH_APP_SCOPES')}</TableCell>
                                <TableCell>{translate('app-settings:OAUTH_APP_ORGANIZATIONS')}</TableCell>
                                <TableCell>{translate('app-settings:OAUTH_APP_REDIRECT_URIS')}</TableCell>
                                <TableCell align="center">{translate('app-settings:OAUTH_APP_ENABLED')}</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {apps.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} align="center">
                                        <Typography variant="body2" color="text.secondary">
                                            {translate('app-settings:OAUTH_APPS_EMPTY')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {apps.map((app) => (
                                <TableRow key={app._id}>
                                    <TableCell>
                                        <Typography variant="body2">{app.name}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{app.vendorName}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontFamily="monospace">
                                            {app.clientId}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={
                                                app.confidential
                                                    ? translate('app-settings:OAUTH_APP_TYPE_CONFIDENTIAL')
                                                    : translate('app-settings:OAUTH_APP_TYPE_PUBLIC')
                                            }
                                            color={app.confidential ? 'primary' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" gap={0.5} flexWrap="wrap">
                                            {app.allowedScopes.map((scope) => (
                                                <Chip key={scope} size="small" label={scope} />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" gap={0.5} flexWrap="wrap">
                                            {app.allowedOrganizations.map((organizationId) => (
                                                <Chip
                                                    key={organizationId}
                                                    size="small"
                                                    label={clinicNamesById.get(organizationId) ?? organizationId}
                                                />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {app.redirectUris.map((uri) => (
                                            <Typography key={uri} variant="body2" fontFamily="monospace">
                                                {uri}
                                            </Typography>
                                        ))}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Switch
                                            checked={app.status === OAuthAppStatus.ACTIVE}
                                            onChange={() => handleToggleStatus(app)}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={translate('app-settings:EDIT_OAUTH_APP')}>
                                            <IconButton size="small" onClick={() => setEditingApp(app)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {app.confidential && (
                                            <Tooltip title={translate('app-settings:ROTATE_SECRET')}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setPendingAction({ type: 'rotate', app })}
                                                >
                                                    <AutorenewIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title={translate('common:DELETE')}>
                                            <IconButton
                                                size="small"
                                                onClick={() => setPendingAction({ type: 'delete', app })}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <EditOAuthAppDialog
                app={editingApp}
                clinics={clinics}
                clinicsLoading={clinicsLoading}
                onCancel={() => setEditingApp(null)}
                onSubmit={handleUpdate}
            />

            <CreateOAuthAppDialog
                open={isCreateOpen}
                clinics={clinics}
                clinicsLoading={clinicsLoading}
                onCancel={() => setIsCreateOpen(false)}
                onSubmit={(input) => {
                    setIsCreateOpen(false);
                    handleCreate(input);
                }}
            />

            <ConfirmDialog
                open={pendingAction !== null}
                handleClose={() => setPendingAction(null)}
                handleConfirm={handleConfirmPending}
                dialogTitle={
                    pendingAction?.type === 'rotate'
                        ? translate('app-settings:ROTATE_SECRET_CONFIRM_TITLE')
                        : translate('app-settings:DELETE_OAUTH_APP_CONFIRM_TITLE')
                }
                dialogContent={
                    pendingAction?.type === 'rotate'
                        ? translate('app-settings:ROTATE_SECRET_CONFIRM_TEXT', {
                              name: pendingAction.app.name,
                          })
                        : pendingAction
                          ? translate('app-settings:DELETE_OAUTH_APP_CONFIRM_TEXT', {
                                name: pendingAction.app.name,
                            })
                          : undefined
                }
            />

            {revealedCredentials && (
                <OAuthAppCredentialsDialog
                    open
                    clientId={revealedCredentials.app.clientId}
                    clientSecret={revealedCredentials.clientSecret}
                    onClose={() => setRevealedCredentials(null)}
                />
            )}
        </Box>
    );
};

export default OAuthApps;
